/**
 * Squarified treemap layout. Pure geometry, no React, no data source.
 *
 * Extracted from MarketHeatmap.js on 2026-09-23 so the portfolio heatmap could
 * reuse it instead of growing a second copy. This is the genuinely hard part of
 * a heatmap and it was already written and working; what differed between the
 * two views was only what a tile MEANS (market cap and today's move, vs. how
 * much of your money is in a position and what it has done since you bought).
 *
 * layoutTreemap(items, rect)
 *   items: [{ size, ...anything }]  -> same objects with x, y, w, h added
 * layoutTwoLevel(groups, w, h)
 *   groups: [{ name, children: [{ size, ... }] }] -> flat cells tagged `sector`
 */

// ── Squarified treemap layout algorithm ──
// Takes flat items [{size, ...}] and a bounding rect {x, y, w, h}
// Returns items with added x, y, w, h for positioning
export function layoutTreemap(items, rect) {
  if (!items.length) return [];
  const totalSize = items.reduce((s, it) => s + it.size, 0);
  if (totalSize <= 0) return [];

  const sorted = [...items].sort((a, b) => b.size - a.size);
  const results = [];

  function worst(row, sideLen) {
    if (!row.length) return Infinity;
    const rowSum = row.reduce((s, it) => s + it._area, 0);
    const maxArea = Math.max(...row.map(it => it._area));
    const minArea = Math.min(...row.map(it => it._area));
    const s2 = sideLen * sideLen;
    return Math.max(
      (s2 * maxArea) / (rowSum * rowSum),
      (rowSum * rowSum) / (s2 * minArea)
    );
  }

  function squarify(children, row, rect) {
    if (!children.length) {
      layoutRow(row, rect);
      return;
    }

    const sideLen = Math.min(rect.w, rect.h);
    const child = children[0];
    const newRow = [...row, child];

    if (!row.length || worst(newRow, sideLen) <= worst(row, sideLen)) {
      squarify(children.slice(1), newRow, rect);
    } else {
      const remaining = layoutRow(row, rect);
      squarify(children, [], remaining);
    }
  }

  function layoutRow(row, rect) {
    const rowSum = row.reduce((s, it) => s + it._area, 0);
    const horizontal = rect.w >= rect.h;
    const sideLen = horizontal ? rect.h : rect.w;
    const rowLen = sideLen > 0 ? rowSum / sideLen : 0;

    let offset = 0;
    for (const item of row) {
      const itemLen = sideLen > 0 ? item._area / rowLen : 0;
      if (horizontal) {
        results.push({ ...item, x: rect.x, y: rect.y + offset, w: rowLen, h: itemLen });
      } else {
        results.push({ ...item, x: rect.x + offset, y: rect.y, w: itemLen, h: rowLen });
      }
      offset += itemLen;
    }

    if (horizontal) {
      return { x: rect.x + rowLen, y: rect.y, w: rect.w - rowLen, h: rect.h };
    } else {
      return { x: rect.x, y: rect.y + rowLen, w: rect.w, h: rect.h - rowLen };
    }
  }

  // Assign normalized areas
  const totalArea = rect.w * rect.h;
  sorted.forEach(it => { it._area = (it.size / totalSize) * totalArea; });

  squarify(sorted, [], rect);
  return results;
}

// Two-level treemap: first lay out sectors, then stocks within each sector
export function layoutTwoLevel(sectors, containerW, containerH) {
  const gap = 2;
  // Lay out sectors
  const sectorRects = layoutTreemap(
    sectors.map(s => ({ name: s.name, size: s.children.reduce((sum, c) => sum + c.size, 0) })),
    { x: 0, y: 0, w: containerW, h: containerH }
  );

  const cells = [];
  sectorRects.forEach(sr => {
    const sector = sectors.find(s => s.name === sr.name);
    if (!sector) return;
    // Inset for gap
    const innerRect = {
      x: sr.x + gap,
      y: sr.y + gap,
      w: Math.max(sr.w - gap * 2, 0),
      h: Math.max(sr.h - gap * 2, 0),
    };
    const stockRects = layoutTreemap(sector.children, innerRect);
    stockRects.forEach(cell => {
      cells.push({ ...cell, sector: sr.name });
    });
  });

  return cells;
}
